import {
    DepositType,
    WithdrawalType,
} from '@core/models/user/enums';
import { getStringPropertyComparator } from '@core/utils/sort/stringPropertyComparator';
import { shellLocalConfig as shellCountryConfigOverrides } from '@countryConfig/shellLocalConfig';
import { TimeFilter } from '@shared/timeFilter/types';
import {
    BonusWalletCategory,
    ShellLocalConfig,
    Logo,
    SiteLingualityKind,
} from '@src/config/shell/localConfigTypes';
import {
    NavigationContext,
    Locale,
} from '@superbet-group/web.types';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { isoCode2ToIsoCode } from '@sportOfferFederation/flags/flagsDefinitions';
import { PromotionHubTab } from '@src/app/promotionHub/shared/types/promotionHubTab';

const shellLocalConfigDefaults: ShellLocalConfig = {
    responsibleGamingPageElements: {
        DepositLimit: false,
        LossLimit: false,
        SessionLimit: false,
        TotalWagerLimit: false,
        SelfExclusion: false,
        TimeOut: false,
        AccountClosure: false,
        RealityCheck: false,
    },
    footer: {
        viberNumber: '',
        hasLegalSectionTitle: false,
        hasAgeWarning: false,
        hasDisclaimer: false,
        hasOnjnLogo: false,
        hasRombetLogo: false,
        hasIbiaLogo: false,
        hasJocResponsabilLogo: false,
        hasBigLogos: false,
        hasEighteenPlus: false,
        hasEighteenPlusCrossed: false,
        hasEighteenPlusNoLink: false,
        hasMgaLogo: false,
        hasBrazilGovLogo: false,
        hasBagoLogo: false,
        hasFpsLogo: false,
        hasGamblingHelpLogo: false,
        hasAlwaysPlayLegallyLogo: false,
        hasPlayResponsiblyText: false,
        hasGamblingTherapyLogo: false,
        hasResponsibleGamingLinks: false,
    },
    header: {
        hasNotificationsIcon: false,
        isBalanceShown: true,
        accountMenuAuthenticated: {
            isRedirect: false,
        },
    },
    contactChannelsEnabled: true,
    bonusWallet: {
        awardFreeSpinsByCount: true,
        categories: {
            [BonusWalletCategory.CardGames]: true,
            [BonusWalletCategory.Casino]: true,
            [BonusWalletCategory.Sport]: true,
            [BonusWalletCategory.Virtuals]: true,
            [BonusWalletCategory.Retail]: true,
            [BonusWalletCategory.Bingo]: false,
        },
    },
    showFooterRightsReserved: false,
    hasLimitsConsent: false,
    getAccountSidebarMenuItems: () => [],
    hasImprovedWithdrawalError: false,
    hasImprovedDepositError: false,
    isUsingErrorCodesTranslations: false,
    isUsingHistoryFrontendTranslations: false,
    isSportTaxVisible: true,
    countryCodesWhitelist: [],
    hasFeatureToggleService: true,
    hasGamingFederation: true,
    hasTicketsFederation: true,
    hasShopsFederation: false,
    hasSocialFederation: false,
    deposit: {},
    withdrawal: {},
    hasEnforcedAmountDecimals: false,
    sseSentryDsn: 'https://c62bbdd350854a57928b813864009ae1@o139620.ingest.sentry.io/4504559132934144',
    logo: Logo.Superbet,
    accountRecovery: false,
    seonConfig: {
        isComtradeFlowSupported: false,
        login: {
            isServiceEnabled: true,
        },
        register: {
            isServiceEnabled: true,
        },
        transactions: {
            isServiceEnabled: true,
            shouldSendWithTransaction: true,
            shouldSendSeparateCall: false,
        },
    },
    isEmailContactShown: true,
    footerContactFormUrl: null,
    bankAccount: {
        length: 18,
        whitelistedPrefix: ['1', '2', '3'],
        allowedCharactersRegex: '[0-9]+',
    },
    hasResponsibleGamingClosureReason: false,
    depositLimit: {
        isDeletable: true,
        monthAllowed: true,
        weekAllowed: true,
        dayAllowed: true,
    },
    lossLimit: {
        isDeletable: true,
        monthAllowed: true,
        weekAllowed: true,
        dayAllowed: true,
    },
    sessionLimit: {
        isDeletable: true,
        monthAllowed: true,
        weekAllowed: true,
        dayAllowed: true,
    },
    defaultSportTimeFilter: TimeFilter.Today,
    defaultPromotionsTimeFilter: [
        TimeFilter.All,
        TimeFilter.Today,
    ],
    defaultTopTenTimeFilter: [
        TimeFilter.All,
        TimeFilter.Today,
    ],
    sportOfferSort: {
        getEventSortRules: () => {
            // TODO: For Belgium we should support dynamically reacting to locale changes,
            // in case this doesn't work out-of-the-box.
            return [
                'sportOrder',
                'specialTournamentOrder',
                getStringPropertyComparator('categoryName'),
                'tournamentOrder',
                getStringPropertyComparator('tournamentName2'),
                getStringPropertyComparator('tournamentName'),
                'date',
                'code',
                'id',
            ];
        },
    },
    offerLocaleOverride: null,
    isTimeOutCustomPeriod: true,
    hasMultipleDomains: false,
    sites: [],
    navigationDestinations: {},
    bottomNavbarItems: [],
    siteLanguageConfig: {
        kind: SiteLingualityKind.SingleLingual,
        locales: [
            {
                name: 'RO',
                value: Locale.Romanian,
                flag: isoCode2ToIsoCode.RO,
            },
            {
                name: 'EN',
                value: Locale.English,
                flag: isoCode2ToIsoCode.GB,
            },
        ],
    },
    sponsoredEntities: null,
    urlLocale: Locale.English,
    bonus: {
        isPromoRewardVisible: false,
        vipUrl: '',
        isPromotionHubEntryPointVisible: true,
        excludedBonusTypes: [],
        availablePromoHubTabs: [
            PromotionHubTab.Offers,
            PromotionHubTab.InProgress,
            PromotionHubTab.Bonuses,
            PromotionHubTab.History,
        ],
    },
    hasTicketsOutsideSportsProduct: true,
    isCompleteAccountActivationModalEnabled: false,
    shouldUseUserUuidForInbox: false,
    shouldShowSessionExpireModal: true,
    myCasinoRouteNames: [],
    neutralNavigationContext: NavigationContext.Sports,
    hasCoinShopAccountMenuLink: false,
    hasResponsibleGamingAccountMenuLinks: false,
    isHeaderHelpIcon: true,
    hasDepositAndWithdrawalAccountMenuLinks: false,
    hasLicencePreferencesAccountMenuLink: false,
    hasDepositHeaderLink: true,
    freeToPlay: {
        useEligibilityChecker: false,
    },
    isScreenOpenAccountOpenSent: false,
    responsibleGamingHasExtraClosureInfo: false,
    arePromotedEventsEnabled: true,
    shouldShowModalsOnResponsibleGamingPage: true,
    defaultDepositTypes: {
        [DepositType.Online]: false,
        [DepositType.OnlinePix]: false,
        [DepositType.Aircash]: false,
        [DepositType.BankTransfer]: false,
        [DepositType.Shop]: false,
        [DepositType.PaySafeCard]: false,
        [DepositType.PaySpot]: false,
        [DepositType.Transaks]: false,
        [DepositType.Bancontact]: false,
        [DepositType.PaymentIQ]: false,
    },
    defaultWithdrawalTypes: {
        [WithdrawalType.Online]: false,
        [WithdrawalType.OnlinePix]: false,
        [WithdrawalType.BankTransfer]: false,
        [WithdrawalType.Instant]: false,
        [WithdrawalType.Shop]: false,
        [WithdrawalType.PaySafeCard]: false,
        [WithdrawalType.Transaks]: false,
        [WithdrawalType.PaymentIQ]: false,
    },
    playerAccountErrorFaqUrl: '',
    isBetBuilderNewBadgeShown: false,
    help: {
        canOpenChatViaUrlHash: true,
        isChatEnabled: true,
    },
    isSportalNewsEnabled: true,
    hasSponsorshipTitle: true,
    hasSponsorshipSubtitle: true,
    responsibleGamblingLoginModal: modalCodes.responsibleGamblingInfo,
    isUsernameDisplayed: true,
    failedLoginAttemptsBeforeForgotPassword: 2,
    hasSportsFederation: true,
    isMultiLoginTarget: false,
    hasBalance: true,
    sportTranslationsUrl: 'https://scorealarm-stats.freetls.fastly.net/localization/superbet/native-apps/sport/android',
};

// Shell specific local config so that we don't have to update `web.lib.config` library for every added field.
export const shellLocalConfig: ShellLocalConfig = {
    ...shellLocalConfigDefaults,
    ...shellCountryConfigOverrides,
};
